import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { AppProvider } from './context/AppContext';
import MainLayout from './components/Layout/MainLayout';
import TableView from './components/Table/TableView';
import TranscriptsView from './components/Transcripts/TranscriptsView';
import TranscriptDetailView from './components/Transcripts/TranscriptDetailView';
import TemplatesView from './components/Templates/TemplatesView';
import ReferencesView from './components/References/ReferencesView';
import ThoughtsView from './components/Thoughts/ThoughtsView';
import PromptsView from './components/Prompts/PromptsView';
import EngineView from './components/Engine/EngineView';
import SurveyView from './components/Survey/SurveyView';
import NewslettersView from './components/Newsletters/NewslettersView';
import OutgoingNewslettersView from './components/Newsletters/OutgoingNewslettersView';
import OutgoingNewsletterEditView from './components/Newsletters/OutgoingNewsletterEditView';
import {
  DEFAULT_NEWSLETTER_EDIT_TAB,
  newsletterEditPath,
  parseNewsletterBlocksViewMode,
  parseNewsletterEditTab,
} from './lib/newsletterPaths';
import ContentCalendarView from './components/Newsletters/ContentCalendarView';
import ArticlesView from './components/Articles/ArticlesView';
import LinkedinView from './components/Linkedin/LinkedinView';
import GoalsView from './components/Goals/GoalsView';
import TasksView from './components/Tasks/TasksView';
import TasksKanbanView from './components/Tasks/TasksKanbanView';
import WorkEpicsView from './components/Tasks/WorkEpicsView';
import SubscribersView from './components/Subscribers/SubscribersView';
import SubscriberDetailView from './components/Subscribers/SubscriberDetailView';
import SiteAnalyticsView from './components/SiteAnalytics/SiteAnalyticsView';
import SubscriberFieldDefinitionsView from './components/Subscribers/SubscriberFieldDefinitionsView';
import ProfileScoreSystemView from './components/Subscribers/ProfileScoreSystemView';
import AutomationsView from './components/Automations/AutomationsView';
import AutomationDetailView from './components/Automations/AutomationDetailView';
import PostmarkTemplatesView from './components/PostmarkTemplates/PostmarkTemplatesView';
import PostmarkTemplateEditView from './components/PostmarkTemplates/PostmarkTemplateEditView';
import PostmarkSignaturesView from './components/PostmarkSignatures/PostmarkSignaturesView';
import ChannelsView from './components/Channels/ChannelsView';
import ChartsView from './components/Charts/ChartsView';
import ElementsView from './components/Elements/ElementsView';
import MembershipTiersView from './components/MembershipTiers/MembershipTiersView';
import CtoWaitlistView from './components/MyCto/CtoWaitlistView';
import CtoMembersView from './components/MyCto/CtoMembersView';
import CtoMemberDetailView from './components/MyCto/CtoMemberDetailView';
import CtoPromptDetailView from './components/MyCto/CtoPromptDetailView';
import CtoReviewsView from './components/MyCto/CtoReviewsView';
import CtoReposView from './components/MyCto/CtoReposView';
import CtoTrainingView from './components/MyCto/CtoTrainingView';
import ChannelsDebugView from './components/Debug/ChannelsDebugView';
import EmailPipelineGuideView from './components/Guide/EmailPipelineGuideView';
import EmailTrackingGuideView from './components/Guide/EmailTrackingGuideView';
import InngestGuideView from './components/Guide/InngestGuideView';
import EdgeFunctionsGuideView from './components/Guide/EdgeFunctionsGuideView';
import SystemMapView from './components/SystemMap/SystemMapView';
import NarrativeArcsView from './components/NarrativeArcs/NarrativeArcsView';
import NarrativeArcDetailPage from './components/NarrativeArcs/NarrativeArcDetailPage';
import CdnAssetsView from './components/CdnAssets/CdnAssetsView';
import GoLinksView from './components/GoLinks/GoLinksView';
import AgentDirectivesView from './components/AgentDirectives/AgentDirectivesView';
import HeldEmailsView from './components/HeldEmails/HeldEmailsView';
import HeldEmailDetailView from './components/HeldEmails/HeldEmailDetailView';
import SiteWritingChatView from './components/SiteWritingChat/SiteWritingChatView';
import ConnectionsView from './components/Connections/ConnectionsView';
import ConnectionDetailView from './components/Connections/ConnectionDetailView';
import AuthForm from './components/Auth/AuthForm';
import AuthCallback from './components/Auth/AuthCallback';
import ScrollToTop from './components/Common/ScrollToTop';
import './App.css';

function CtoTrainingPathRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/cto/frameworks/${id}` : '/cto/frameworks'} replace />;
}

/** `/outgoing-newsletters/:id` → `/outgoing-newsletters/:id/content` (honors legacy `?tab=` and `?view=`). */
function NewsletterEditTabRedirect() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  if (!id) {
    return <Navigate to="/outgoing-newsletters" replace />;
  }
  const rawTab = searchParams.get('tab');
  const tab = parseNewsletterEditTab(rawTab === 'preview' ? 'content' : rawTab);
  const view = parseNewsletterBlocksViewMode(
    searchParams.get('view') ?? (rawTab === 'preview' ? 'preview' : null),
  );
  return <Navigate to={newsletterEditPath(id, tab, { view })} replace />;
}

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 rounded-full border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <AppProvider>
        <Routes>
          <Route path="/login" element={<AuthForm />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/blocks"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TableView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transcripts/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TranscriptDetailView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transcripts"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TranscriptsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/held-emails/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <HeldEmailDetailView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/held-emails"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <HeldEmailsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/writing-chat"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SiteWritingChatView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/connections/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ConnectionDetailView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/connections"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ConnectionsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TemplatesView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/references"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ReferencesView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/thoughts"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ThoughtsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/survey"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SurveyView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/newsletters"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <NewslettersView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/outgoing-newsletters"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <OutgoingNewslettersView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/outgoing-newsletters/new"
            element={<Navigate to={newsletterEditPath('new', DEFAULT_NEWSLETTER_EDIT_TAB)} replace />}
          />
          <Route
            path="/outgoing-newsletters/new/:tab"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <OutgoingNewsletterEditView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/outgoing-newsletters/:id/:tab"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <OutgoingNewsletterEditView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/outgoing-newsletters/:id"
            element={
              <ProtectedRoute>
                <NewsletterEditTabRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/content-calendar"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ContentCalendarView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/articles"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ArticlesView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/linkedin"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <LinkedinView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/goals"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <GoalsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TasksView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/board"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TasksKanbanView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/epics"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <WorkEpicsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscribers"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SubscribersView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/site-analytics"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SiteAnalyticsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscribers/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SubscriberDetailView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/channels"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ChannelsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/charts"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ChartsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/elements"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ElementsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriber-field-definitions"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SubscriberFieldDefinitionsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriber-profile-scoring"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ProfileScoreSystemView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/automations"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AutomationsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/automations/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AutomationDetailView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/postmark-templates"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <PostmarkTemplatesView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/postmark-templates/new"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <PostmarkTemplateEditView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/postmark-templates/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <PostmarkTemplateEditView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/postmark-signatures"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <PostmarkSignaturesView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/membership-tiers"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <MembershipTiersView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cto/waitlist"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CtoWaitlistView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cto/members"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CtoMembersView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cto/reviews"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CtoReviewsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/cto/repos" element={<Navigate to="/cto/codebases" replace />} />
          <Route
            path="/cto/codebases"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CtoReposView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cto/members/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CtoMemberDetailView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cto/frameworks"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CtoTrainingView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cto/frameworks/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CtoPromptDetailView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cto/training"
            element={<Navigate to="/cto/frameworks" replace />}
          />
          <Route
            path="/cto/training/:id"
            element={<CtoTrainingPathRedirect />}
          />
          <Route
            path="/prompts"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <PromptsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/debug/channels"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ChannelsDebugView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/guide/email-pipeline"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <EmailPipelineGuideView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/guide/email-tracking"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <EmailTrackingGuideView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/guide/inngest"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <InngestGuideView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/guide/edge-functions"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <EdgeFunctionsGuideView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent-directives"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AgentDirectivesView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cdn"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CdnAssetsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/go"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <GoLinksView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-map"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SystemMapView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/narrative-arcs"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <NarrativeArcsView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/narrative-arcs/:slug"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <NarrativeArcDetailPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <EngineView />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppProvider>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;